import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography, Select, MenuItem, FormControl, Button } from '@mui/material';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { StatusTable } from '../../../common/components/StatusTable/StatusTable';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import { useE09Status } from './useE09Status';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { StyledSegmentedControl, StyledSegmentButton } from '../../../common/components/SectionBar/SectionBar.styled';

import { StyledContainer, MainContent, TableSection } from './e09Status.styled';

const E09Status: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/e09/status', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // Local filter state for form inputs (before submit)
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedPpo, setSelectedPpo] = useState<string>('');

  // Get effective operators + cascading logic for e09 module
  const {
    operators,
    availableYears,
    operatorsForYear,
    relationsData,
    isPpoLoading: isPposLoading,
    ppoError,
  } = useModuleOperators('e09', { selectedYear, selectedPpo });

  // Convert years to strings for dropdown
  const years = availableYears.map(year => year.toString());

  // State for filters from URL parameters
  const [yearFilter, setYearFilter] = useState<string | undefined>(undefined);
  const [ppoFilter, setPpoFilter] = useState<string | undefined>(undefined);
  const [inboundFilter, setInboundFilter] = useState<boolean | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState<boolean | undefined>(undefined);

  const [selectedDirection, setSelectedDirection] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  // Section bar form state (separate from API filters)
  const [sectionBarYear, setSectionBarYear] = useState<string>('');
  const [sectionBarPpo, setSectionBarPpo] = useState<string>('');
  const [sectionBarDirection, setSectionBarDirection] = useState<string>('all');
  const [sectionBarStatus, setSectionBarStatus] = useState<string>('all');

  // Initialize section bar state when hasSubmitted becomes true
  useEffect(() => {
    if (hasSubmitted) {
      setSectionBarYear(selectedYear);
      setSectionBarPpo(selectedPpo);
      setSectionBarDirection(selectedDirection);
      setSectionBarStatus(selectedStatus);
    }
  }, [hasSubmitted, selectedYear, selectedPpo, selectedDirection, selectedStatus]);

  // Filter handling functions
  const handleSubmit = () => {
    // Convert form values to API parameters
    const year = selectedYear || undefined;
    const ppo = selectedPpo || undefined;
    
    let inbound: boolean | undefined = undefined;
    if (selectedDirection === 'inbound') inbound = true;
    else if (selectedDirection === 'outbound') inbound = false;
    
    let open: boolean | undefined = undefined;
    if (selectedStatus === 'open') open = true;
    else if (selectedStatus === 'closed') open = false;

    // Update filters which will trigger API call
    setYearFilter(year);
    setPpoFilter(ppo);
    setInboundFilter(inbound);
    setOpenFilter(open);
    setHasSubmitted(true);
    
    // Force refetch to ensure request is made even if filters haven't changed
    refetch();
  };

  const handleSectionBarSubmit = () => {
    // Convert section bar form values to API parameters
    const year = sectionBarYear || undefined;
    const ppo = sectionBarPpo || undefined;
    
    let inbound: boolean | undefined = undefined;
    if (sectionBarDirection === 'inbound') inbound = true;
    else if (sectionBarDirection === 'outbound') inbound = false;
    
    let open: boolean | undefined = undefined;
    if (sectionBarStatus === 'open') open = true;
    else if (sectionBarStatus === 'closed') open = false;

    // Update filters which will trigger API call
    setYearFilter(year);
    setPpoFilter(ppo);
    setInboundFilter(inbound);
    setOpenFilter(open);
    
    // Force refetch to ensure request is made even if filters haven't changed
    refetch();
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = (value: string) => {
    setSelectedPpo(value);
  };

  // Handle year selection change with cascading behavior
  const handleYearChange = (value: string) => {
    if (selectedYear !== value && selectedPpo) {
      const relationsForNewYear = relationsData?.byYear?.[value];
      const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === selectedPpo &&
        relation.modules.some(module => 
          module.name === 'e09' && module.enabled
        )
      );
      if (!isCurrentDOAvailableForNewYear) {
        setSelectedPpo('');
      }
    }
    setSelectedYear(value);
  };

  const handleReset = () => {
    setSelectedYear('');
    setSelectedPpo('');
    setSelectedDirection('all');
    setSelectedStatus('all');
    setYearFilter(undefined);
    setPpoFilter(undefined);
    setInboundFilter(undefined);
    setOpenFilter(undefined);
    setHasSubmitted(false);
  };

  const handleSectionBarReset = () => {
    setSectionBarYear('');
    setSectionBarPpo('');
    setSectionBarDirection('all');
    setSectionBarStatus('all');
    setYearFilter(undefined);
    setPpoFilter(undefined);
    setInboundFilter(undefined);
    setOpenFilter(undefined);
    refetch();
  };

  // Use the E09Status hook with the filters
  const { 
    data: statusResponseData, 
    isLoading: statusLoading, 
    isError: statusError, 
    error: statusErrorDetails,
    refetch 
  } = useE09Status({
    year: yearFilter,
    ppo: ppoFilter,
    inbound: inboundFilter,
    open: openFilter
  });

  // Debug logging to see what's happening with the filters
  useEffect(() => {
    console.log('E09 Status Debug:', {
      hasSubmitted,
      selectedDirection,
      sectionBarDirection,
      inboundFilter,
      yearFilter,
      ppoFilter,
      openFilter,
      statusResponseData: statusResponseData ? 'Has data' : 'No data'
    });
  }, [hasSubmitted, selectedDirection, sectionBarDirection, inboundFilter, yearFilter, ppoFilter, openFilter, statusResponseData]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Show loading indicator
  const showLoading = statusLoading || permissionLoading || userLoading;

  // Create custom filters for SectionBar when results are shown
  const customFiltersForSectionBar = useMemo(() => {
    if (!hasSubmitted) return undefined;

    return {
      e09StatusPpo: sectionBarPpo,
      e09StatusYear: sectionBarYear,
      e09StatusDirection: sectionBarDirection,
      e09StatusStatus: sectionBarStatus,
      e09StatusPpoOptions: (sectionBarYear ? operatorsForYear : operators).map(targetDO => ({
        id: targetDO,
        name: targetDO,
        disabled: false
      })),
      e09StatusYearOptions: years,
      e09StatusIsPpoLoading: isPposLoading,
      onE09StatusPpoChange: setSectionBarPpo,
      onE09StatusYearChange: setSectionBarYear,
      onE09StatusDirectionChange: setSectionBarDirection,
      onE09StatusStatusChange: setSectionBarStatus,
      onE09StatusSubmit: handleSectionBarSubmit,
      onE09StatusReset: handleSectionBarReset,
      e09StatusIsSubmitting: statusLoading,
    };
  }, [
    hasSubmitted,
    sectionBarPpo,
    sectionBarYear,
    sectionBarDirection,
    sectionBarStatus,
    operators,
    operatorsForYear,
    years,
    isPposLoading,
    handleSectionBarSubmit,
    handleSectionBarReset,
    statusLoading,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='e09-status-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='e09 Status'
            subtitle='e09 IBRS'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              customE09Status: !!hasSubmitted,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="e09-status-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='e09-status-main-content'>
            {showLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="loading-indicator">
                <CircularProgress />
              </Box>
            ) : statusError ? (
              <Alert severity='error' data-testid="error-alert">
                Error loading data: {statusErrorDetails instanceof Error ? statusErrorDetails.message : 'Unknown error'}
              </Alert>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="permission-error-alert">You don't have permission to view this section</Alert>
            ) : (
              <>
                {/* Filter Section - Hide when results are shown */}
                {!hasSubmitted && (
                  <Box sx={{ p: 3, backgroundColor: '#FFFFFF', borderRadius: 1, mb: 3, maxWidth: '100%' }}>
                    <Grid container spacing={2} p="40px">
                      {/* PPO Dropdown - Full width */}
                      <Typography variant="h3" sx={{ 
                        mb: 1, 
                        mt: 1,
                        ml: 2,
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#063142'
                      }}>
                        e09 Status
                      </Typography>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                          DO
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedPpo}
                            onChange={(e) => handleDOChange(e.target.value)}
                            displayEmpty
                            sx={{ 
                              backgroundColor: 'white',
                              maxWidth: '200px',
                              '& .MuiSelect-select': {
                                padding: '12px 14px',
                              }
                            }}
                          >
                            <MenuItem value="">ALL</MenuItem>
                            {!isPposLoading &&
                              !ppoError &&
                              (selectedYear ? operatorsForYear : operators).map((targetDO) => (
                                <MenuItem key={targetDO} value={targetDO}>
                                  {targetDO}
                                </MenuItem>
                              ))}
                            {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Year Dropdown - Full width */}
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                          Year
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedYear}
                            onChange={(e) => handleYearChange(e.target.value)}
                            displayEmpty
                            sx={{ 
                              backgroundColor: 'white',
                              maxWidth: '200px',
                              '& .MuiSelect-select': {
                                padding: '12px 14px',
                              }
                            }}
                          >
                            <MenuItem value="">ALL</MenuItem>
                            {years.map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Inbound/Outbound Filter */}
                      <Grid item xs="auto">
                        <StyledSegmentedControl sx={{ mb: 2, width: '208px' }}>
                          <StyledSegmentButton
                            active={selectedDirection === 'all'}
                            onClick={() => setSelectedDirection('all')}
                            sx={{ color: selectedDirection === 'all' ? '#ffffff' : '#00B6BA' }}
                          >
                            All
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedDirection === 'inbound'}
                            onClick={() => setSelectedDirection('inbound')}
                            sx={{ color: selectedDirection === 'inbound' ? '#ffffff' : '#00B6BA' }}
                          >
                            Inbound
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedDirection === 'outbound'}
                            onClick={() => setSelectedDirection('outbound')}
                            sx={{ color: selectedDirection === 'outbound' ? '#ffffff' : '#00B6BA' }}
                          >
                            Outbound
                          </StyledSegmentButton>
                        </StyledSegmentedControl>
                      </Grid>

                      {/* Open/Closed Filter */}
                      <Grid item xs="auto">
                        <StyledSegmentedControl sx={{ mb: 3, width: '168px' }}>
                          <StyledSegmentButton
                            active={selectedStatus === 'all'}
                            onClick={() => setSelectedStatus('all')}
                            sx={{ color: selectedStatus === 'all' ? '#ffffff' : '#00B6BA' }}
                          >
                            All
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedStatus === 'open'}
                            onClick={() => setSelectedStatus('open')}
                            sx={{ color: selectedStatus === 'open' ? '#ffffff' : '#00B6BA' }}
                          >
                            Open
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedStatus === 'closed'}
                            onClick={() => setSelectedStatus('closed')}
                            sx={{ color: selectedStatus === 'closed' ? '#ffffff' : '#00B6BA' }}
                          >
                            Closed
                          </StyledSegmentButton>
                        </StyledSegmentedControl>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleReset}
                            sx={{
                              minWidth: '100px',
                              borderRadius: '1000px',
                              height: '40px',
                              borderColor: '#dee2e6',
                              color: '#6c757d',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              '&:hover': {
                                borderColor: '#adb5bd',
                                backgroundColor: '#e9ecef'
                              }
                            }}
                          >
                            RESET
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                              minWidth: '100px',
                              borderRadius: '1000px',
                              height: '40px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              '&:hover': {
                                backgroundColor: '#5a6268'
                              }
                            }}
                          >
                            SUBMIT
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}



                {/* Status Tables - Only show after submit */}
                {hasSubmitted && (
                  <StyledContainer data-testid="tables-container">
                    {/* Show Inbound Table only when direction is 'all' or 'inbound' */}
                    {(inboundFilter === undefined || inboundFilter === true) && (
                      <TableSection data-testid="inbound-status-section" sx={{ mb: inboundFilter === undefined ? 0 : undefined }}>
                        <StatusTable 
                          data={statusResponseData?.inbound || []}
                          title={`Inbound from * to ${userData?.designatedOperator}`}
                          showQuarters={['q1', 'q2', 'q3', 'q4']}
                          backgroundColor="#F0FFF0"
                        />
                      </TableSection>
                    )}
                    
                    {/* Show Outbound Table only when direction is 'all' or 'outbound' */}
                    {(inboundFilter === undefined || inboundFilter === false) && (
                      <TableSection data-testid="outbound-status-section">
                        <StatusTable 
                          data={statusResponseData?.outbound || []}
                          title={`Outbound from ${userData?.designatedOperator} to *`}
                          showQuarters={['q1', 'q2', 'q3', 'q4']}
                          backgroundColor="#FFF0F0"
                        />
                      </TableSection>
                    )}
                  </StyledContainer>
                )}
              </>
            )}

            {/* Notification Snackbar */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="notification-snackbar"
            />
          </MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default E09Status;
